/**
 * Template Import/Export Utilities
 * Export templates as ZIP files with images and JSON structure
 * Import templates from ZIP files
 */

import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import config from '@/lib/config';
import { client } from '@/lib/hono';
import type { InvoiceTemplate, TemplateElement, ContainerElement } from '../types/template.types';

/**
 * Remove cellAlignments from container and all nested containers recursively
 */
function removeCellAlignments(container: ContainerElement): ContainerElement {
  const { cellAlignments, ...containerWithoutCellAlignments } = container;
  
  // Recursively process children
  if (container.children && container.children.length > 0) {
    containerWithoutCellAlignments.children = container.children.map(child => {
      if (child.element.type === 'container') {
        return {
          ...child,
          element: removeCellAlignments(child.element as ContainerElement),
        };
      }
      return child;
    });
  }
  
  return containerWithoutCellAlignments;
}

/**
 * Export template as ZIP file
 * Contains template.json and all referenced images
 */
export async function exportTemplate(template: InvoiceTemplate): Promise<void> {
  const zip = new JSZip();
  
  // Extract structure from rootContainer
  const structure = template.rootContainer?.containers || [];
  const headerStructure = template.rootContainer?.header;
  const footerStructure = template.rootContainer?.footer;
  
  // Remove cellAlignments from all containers before export
  const cleanedStructure = Array.isArray(structure) 
    ? structure.map(c => removeCellAlignments(c))
    : (structure ? removeCellAlignments(structure as ContainerElement) : undefined);
  const cleanedHeader = headerStructure ? removeCellAlignments(headerStructure) : undefined;
  const cleanedFooter = footerStructure ? removeCellAlignments(footerStructure) : undefined;
  
  // Prepare template data (remove server-specific fields and cellAlignments)
  const templateData = {
    name: template.name,
    isActive: template.isActive,
    fontFamily: template.fontFamily,
    fontSize: template.fontSize,
    fontColor: template.fontColor,
    logoUrl: template.logoUrl,
    structure: cleanedStructure,
    headerStructure: cleanedHeader,
    footerStructure: cleanedFooter,
    leftMargin: template.rootContainer?.leftMargin,
    rightMargin: template.rootContainer?.rightMargin,
    topMargin: template.rootContainer?.topMargin,
    bottomMargin: template.rootContainer?.bottomMargin,
  };
  
  // Add template JSON
  zip.file('template.json', JSON.stringify(templateData, null, 2));
  
  // Collect all image URLs from the template
  const imageUrls = new Set<string>();
  
  const collectImageUrls = (elements: TemplateElement[]) => {
    if (!elements) return;
    
    for (const element of elements) {
      // Check for image elements
      if ((element.type === 'image' || element.type === 'logo') && element.imageUrl) {
        imageUrls.add(element.imageUrl);
      }
      
      // Recursively check container children
      if (element.type === 'container' && element.children) {
        collectImageUrls(element.children.map(c => c.element));
      }
    }
  };
  
  // Collect from all structures
  const structureArray = Array.isArray(structure) ? structure : (structure ? [structure] : []);
  structureArray.forEach(container => collectImageUrls([container]));
  if (headerStructure) collectImageUrls([headerStructure]);
  if (footerStructure) collectImageUrls([footerStructure]);
  
  // Download and add images to ZIP
  const imageFolder = zip.folder('images');
  if (imageFolder) {
    let imageIndex = 0;
    const imageMapping: Record<string, string> = {};
    
    for (const imageUrl of imageUrls) {
      try {
        // Resolve absolute URL so exports work both locally and in production
        const resolvedUrl = imageUrl.startsWith('http')
          ? imageUrl
          : `${config.apiUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;

        // Fetch image
        const response = await fetch(resolvedUrl, { credentials: 'include' });
        if (!response.ok) continue;
        
        const blob = await response.blob();
        
        // Determine file extension
        const extension = blob.type.split('/')[1] || 'png';
        const filename = `image_${imageIndex}.${extension}`;
        
        // Add to ZIP
        imageFolder.file(filename, blob);
        
        // Store mapping for JSON update
        imageMapping[imageUrl] = `images/${filename}`;
        imageIndex++;
      } catch (error) {
        console.error('Failed to download image:', imageUrl, error);
      }
    }
    
    // Update template JSON with local image paths
    const updateImagePaths = (elements: TemplateElement[]) => {
      if (!elements) return;
      
      for (const element of elements) {
        if ((element.type === 'image' || element.type === 'logo') && element.imageUrl) {
          if (imageMapping[element.imageUrl]) {
            element.imageUrl = imageMapping[element.imageUrl];
          }
        }
        
        if (element.type === 'container' && element.children) {
          updateImagePaths(element.children.map(c => c.element));
        }
      }
    };
    
    const structureArr = Array.isArray(templateData.structure) ? templateData.structure : (templateData.structure ? [templateData.structure] : []);
    structureArr.forEach(container => updateImagePaths([container]));
    if (templateData.headerStructure) updateImagePaths([templateData.headerStructure]);
    if (templateData.footerStructure) updateImagePaths([templateData.footerStructure]);
    
    // Update template JSON with new paths
    zip.file('template.json', JSON.stringify(templateData, null, 2));
  }
  
  // Generate and download ZIP
  const blob = await zip.generateAsync({ type: 'blob' });
  const filename = `${template.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_template.zip`;
  saveAs(blob, filename);
}

/**
 * Import template from ZIP file
 * Returns template data and image files that need to be uploaded
 */
export async function importTemplate(file: File): Promise<{
  template: Partial<InvoiceTemplate>;
  images: Array<{ path: string; file: Blob; originalPath: string }>;
}> {
  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);
  
  // Read template.json
  const templateFile = zipContent.file('template.json');
  if (!templateFile) {
    throw new Error('template.json nicht in ZIP-Datei gefunden');
  }
  
  const templateJson = await templateFile.async('text');
  const templateData = JSON.parse(templateJson);
  
  // Reconstruct template with proper structure
  const template: Partial<InvoiceTemplate> = {
    name: templateData.name,
    isActive: templateData.isActive,
    fontFamily: templateData.fontFamily,
    fontSize: templateData.fontSize,
    fontColor: templateData.fontColor,
    logoUrl: templateData.logoUrl,
    rootContainer: {
      header: templateData.headerStructure,
      footer: templateData.footerStructure,
      containers: templateData.structure,
      leftMargin: templateData.leftMargin,
      rightMargin: templateData.rightMargin,
      topMargin: templateData.topMargin,
      bottomMargin: templateData.bottomMargin,
    },
  };
  
  // Collect images
  const images: Array<{ path: string; file: Blob; originalPath: string }> = [];
  const imageFiles = zipContent.folder('images');
  
  if (imageFiles) {
    const promises: Promise<void>[] = [];
    
    zipContent.forEach((relativePath, zipEntry) => {
      if (relativePath.startsWith('images/') && !zipEntry.dir) {
        promises.push(
          zipEntry.async('blob').then((blob) => {
            images.push({
              path: relativePath.replace('images/', ''),
              file: blob,
              originalPath: relativePath,
            });
          })
        );
      }
    });
    
    await Promise.all(promises);
  }
  
  return { template, images };
}

/**
 * Upload images and update template structure with new URLs
 */
export async function uploadImagesAndUpdateTemplate(
  template: Partial<InvoiceTemplate>,
  images: Array<{ path: string; file: Blob; originalPath: string }>
): Promise<Partial<InvoiceTemplate>> {
  const imageMapping: Record<string, string> = {};
  const token = localStorage.getItem('auth-data');
  
  // Upload each image
  for (const image of images) {
    const formData = new FormData();
    formData.append('file', image.file, image.path);
    
    try {
      // Use direct fetch for FormData uploads (Hono client has issues with FormData)
      const response = await fetch(`${config.apiUrl}/api/invoice-template/upload-image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || `Upload failed for ${image.path}`);
      }
      
      const data = await response.json();
      // Ensure URL is absolute for canvas rendering
      const absoluteUrl = data.url.startsWith('http') ? data.url : `${config.apiUrl}${data.url}`;
      imageMapping[image.originalPath] = absoluteUrl;
    } catch (error) {
      console.error('Failed to upload image:', image.path, error);
      throw error;
    }
  }
  
  // Update image URLs in template structure
  const updateImageUrls = (elements: TemplateElement[]) => {
    if (!elements) return;
    
    for (const element of elements) {
      if ((element.type === 'image' || element.type === 'logo') && element.imageUrl) {
        if (imageMapping[element.imageUrl]) {
          element.imageUrl = imageMapping[element.imageUrl];
        }
      }
      
      if (element.type === 'container' && element.children) {
        updateImageUrls(element.children.map(c => c.element));
      }
    }
  };
  
  // Update in structure, headerStructure, footerStructure
  const templateWithStructure = template as Partial<InvoiceTemplate> & {
    structure?: ContainerElement | ContainerElement[];
    headerStructure?: ContainerElement;
    footerStructure?: ContainerElement;
    leftMargin?: number;
    rightMargin?: number;
    topMargin?: number;
    bottomMargin?: number;
  };
  
  const structure = templateWithStructure.structure;
  const headerStructure = templateWithStructure.headerStructure;
  const footerStructure = templateWithStructure.footerStructure;
  
  const structureArr = Array.isArray(structure) ? structure : (structure ? [structure] : []);
  structureArr.forEach((container: TemplateElement) => updateImageUrls([container]));
  if (headerStructure) updateImageUrls([headerStructure]);
  if (footerStructure) updateImageUrls([footerStructure]);
  
  return template;
}
